import { TextCapitalized, Spacer, triggerToast, TextAreaDS } from "@qivia/ui";
import { useState, useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../../../redux/hooks";
import {
  selectVehicleNoteTakingUpdatedStatus,
  updateVehicleNoteTakingAsync,
  vehiclesSlice,
} from "../vehiclesSlice";
import styled from "styled-components";
import { typographies } from "@qivia/ui/src/styles/figmaTypographies";
import { colors } from "@qivia/ui/src/styles/figmaColors";
import { selectCompany } from "../../homeSlice";

export const NoteTakingCodeSection = (props: {
  noteTaking: string | null;
  vehicleUuid: string;
  vehicleUpdatedAt: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [noteTaking, setNoteTaking] = useState<string>(props.noteTaking ?? "");
  const [displayError, setDisplayError] = useState<boolean>(false);
  const company = useAppSelector(selectCompany);
  const vehicleNoteTakingUpdatedStatus = useAppSelector(
    selectVehicleNoteTakingUpdatedStatus,
  );

  const hasLimitCharacterReached: boolean = useMemo(() => {
    if (0 <= noteTaking.length && noteTaking.length <= 300) return false;
    return true;
  }, [noteTaking]);

  const isValidNoteTakingInput: boolean = useMemo(() => {
    return !hasLimitCharacterReached;
  }, [hasLimitCharacterReached]);

  useEffect(() => {
    if (vehicleNoteTakingUpdatedStatus === "success") {
      triggerToast(t(`vehicles.updatedNoteTaking.success`) || "", "valid");
      dispatch(vehiclesSlice.actions.resetVehicleUpdatedNoteTakingStatus());
    } else if (vehicleNoteTakingUpdatedStatus === "failed") {
      triggerToast(t("vehicles.updatedNoteTaking.failure") || "", "error");
      dispatch(vehiclesSlice.actions.resetVehicleUpdatedNoteTakingStatus());
    }
  }, [dispatch, vehicleNoteTakingUpdatedStatus, t]);

  const onClick = useCallback(() => {
    if (!company) {
      return;
    }
    setDisplayError(true);
    if (isValidNoteTakingInput) {
      void dispatch(
        updateVehicleNoteTakingAsync({
          vehicleUuid: props.vehicleUuid,
          noteTaking,
          companyUuid: company.uuid,
        }),
      );
      setDisplayError(false);
    }
  }, [
    dispatch,
    isValidNoteTakingInput,
    noteTaking,
    company,
    props.vehicleUuid,
  ]);

  const noteTakingError: string | undefined = useMemo(() => {
    return displayError && hasLimitCharacterReached
      ? t("vehicles.sidepanel.noteTaking.error.limitReached")
      : undefined;
  }, [displayError, hasLimitCharacterReached, t]);

  return (
    <StyledNote>
      <TextCapitalized>
        {t("vehicles.sidepanel.noteTaking.title")}
      </TextCapitalized>
      <Spacer y={0.25} />
      <StyledNoteDate>
        {noteTaking ? props.vehicleUpdatedAt : <Spacer y={1} />}
      </StyledNoteDate>
      <Spacer y={1} />
      <TextAreaDS
        placeholder={t("vehicles.sidepanel.noteTaking.placeholder")}
        value={noteTaking}
        $update={(e: string) => setNoteTaking(e)}
        error={noteTakingError}
        onBlur={() => onClick()}
      />
    </StyledNote>
  );
};

const StyledNoteDate = styled.div`
  display: flex;
  ${typographies["Body/XXS"]}
  color: ${colors["colors/text/darkGrey"]};
`;

const StyledNote = styled.div`
  display: flex;
  flex-direction: column;
  ${typographies["Body/L"]}
  color: ${colors["colors/text/black"]}
`;
